import React from 'react'
import { useTranslation } from 'react-i18next'
import { type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import Accordion from '~/components/Accordion'
import Container from '~/components/Container'
import ExternalLink from '~/components/icons/ExternalLink'
import Link from '~/components/Link'
import Page from '~/components/Page'
import Typography from '~/components/Typography'
import { useTrackPageView } from '~/hooks/useTrackPageView'
import { getFixedT } from '~/i18n/i18next.server'
import { buildMetas } from '~/utils/metas'
import Investors from './Investors'
import { EMPLOYEES } from './team.constants'
import team from './team.png'

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { t } = await getFixedT(request)

  return {
    title: t('common:theTeam')
  }
}

export const meta: MetaFunction<typeof loader> = ({
  location,
  params,
  data
}) => {
  return buildMetas({
    location,
    params,
    title: data?.title
  })
}

export const handle = {
  i18n: ['team']
}

const TeamRoute = () => {
  const { t } = useTranslation(['team'])
  useTrackPageView('Team')

  return (
    <Page
      disableContainer
      className="max-lg:!pb-0"
      title={t('team:whoAreWe')}
      subtitle={t('common:theTeam')}
    >
      <Container fluid>
        <img
          className="w-full lg:rounded-md"
          src={team}
          alt={t('team:imageAlt')}
          loading="lazy"
        />
      </Container>
      <Container className="my-20">
        <section className="flex flex-col gap-y-8">
          <div className="flex flex-col gap-y-8">
            <Typography component="h2" variant="h3" className="font-normal">
              {t('team:managementTeam')}
            </Typography>
            <Typography
              component="p"
              variant="body1"
              className="max-w-xl"
              color="shy"
            >
              {t('team:managementTeamDescription')}
            </Typography>
            <Typography className="text-black/50" variant="subtitle2">
              {t('team:legendTeam')}
            </Typography>
          </div>
          <div className="flex flex-col">
            {EMPLOYEES.map((employee) => {
              const title = t(employee.i18nTitleKey)
              const resumee = t(employee.i18nResumeeKey)

              return (
                <Accordion
                  key={employee.name}
                  className="px-5"
                  separator="top"
                  title={
                    <div className="flex gap-x-24 gap-y-1 max-md:flex-col">
                      <Typography
                        component="h4"
                        className="w-1/5 max-md:w-full"
                      >
                        {employee.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        component="p"
                        className="text-black/40"
                      >
                        {title}
                      </Typography>
                    </div>
                  }
                  testId={employee.i18nTitleKey}
                >
                  <div className="flex flex-col gap-y-5">
                    <Typography
                      variant="body2"
                      align="left"
                      className="text-black/80 max-w-4xl"
                    >
                      {resumee}
                    </Typography>
                    {employee.linkedinUrl ? (
                      <Link
                        to={employee.linkedinUrl}
                        testId="linkedIn"
                        target="_blank"
                        underline="hover"
                        rel="noreferrer"
                        className="flex items-center gap-x-1 text-nowrap"
                      >
                        LinkedIn
                        <ExternalLink type="text" />
                      </Link>
                    ) : null}
                  </div>
                </Accordion>
              )
            })}
          </div>
        </section>
      </Container>
      <Container fluid>
        <Investors />
      </Container>
    </Page>
  )
}

export default TeamRoute
